import type { FunctionComponent } from 'react';
import { FormattedMessage } from 'react-intl';

import { Analytics } from '@trello/atlassian-analytics';
import { boardSwitcherSharedState } from '@trello/board-switcher';
import { useSharedState } from '@trello/shared-state';

import { getWorkspaceViewUrl } from 'app/scripts/controller/urls';
import { useMemberWorkspaceRelationship } from 'app/src/components/WorkspaceNavigation/useMemberWorkspaceRelationship';
import { useWorkspaceViews } from 'app/src/components/WorkspaceNavigation/useWorkspaceViews';
import { BoardSwitcherTiles } from './BoardSwitcherTiles';
import { CreateWorkspaceViewButton } from './CreateWorkspaceViewButton';
import { ExpandSection } from './ExpandSection';
import { getBoardSwitcherSource } from './getBoardSwitcherSource';
import { WorkspaceViewTile } from './WorkspaceViewTile';

interface WorkspaceViewsProps {
  workspaceId: string;
  enterpriseId?: string;
}

export const WorkspaceViews: FunctionComponent<WorkspaceViewsProps> = ({
  workspaceId,
  enterpriseId,
}) => {
  const [{ expandSectionsState }] = useSharedState(boardSwitcherSharedState);

  const { userRelationshipToWorkspace } = useMemberWorkspaceRelationship({
    workspaceId,
  });

  const {
    hasWorkspaceViewsFeature,
    views: defaultViews,
    organizationViews,
  } = useWorkspaceViews({
    workspaceId: workspaceId ?? '',
    userRelationshipToWorkspace: userRelationshipToWorkspace ?? 'NONE',
    skip: !workspaceId,
  });

  if (!hasWorkspaceViewsFeature) {
    return null;
  }

  const onClick = (viewType = 'custom', isDefaultView: boolean) => {
    Analytics.sendClickedLinkEvent({
      linkName: 'workspaceViewTileLink',
      source: getBoardSwitcherSource(),
      attributes: {
        viewType,
        isDefaultView,
      },
    });

    boardSwitcherSharedState.setValue({ isOpen: false });
  };

  const expandSectionId = `views-${workspaceId}`;
  const isExpandedByDefault = expandSectionsState[expandSectionId] ?? false;

  return (
    <ExpandSection
      key={expandSectionId}
      id={expandSectionId}
      title={
        <FormattedMessage
          id="templates.board_switcher.workspace-views"
          defaultMessage="Workspace Views"
          description="Title of the workspace views section of a workspace on the board switcher"
        />
      }
      isExpandedByDefault={isExpandedByDefault}
      actions={
        <CreateWorkspaceViewButton
          workspaceId={workspaceId}
          enterpriseId={enterpriseId}
        />
      }
    >
      <BoardSwitcherTiles>
        {defaultViews?.map(({ title, href, viewType }) => (
          <WorkspaceViewTile
            key={title}
            id={`${viewType}DefaultView`}
            title={title}
            href={href}
            type={viewType}
            onClick={() => onClick(viewType, true)}
          />
        ))}
        {organizationViews?.map(({ id, name, shortLink, prefs, views }) => (
          <WorkspaceViewTile
            key={id}
            id={id}
            title={name}
            href={getWorkspaceViewUrl({ shortLink, name })}
            type={views?.[0]?.defaultViewType.toLowerCase()}
            onClick={() => onClick('custom', false)}
          />
        ))}
      </BoardSwitcherTiles>
    </ExpandSection>
  );
};
